import { Injectable } from '@angular/core';
import {
  CallPROSolarmanDeviceDetailsResponse,
  CallPROSolarmanFirmwareDetailsResponse,
  Communication,
  SolarmanDeviceStatus,
} from 'ngx-modbus';

import { SolarmanService } from './solarman.service';

const UPG_DEBUG = { ENABLED: false };
const upgLog = (msg: string, data?: unknown) => UPG_DEBUG.ENABLED && console.log(`[Modbus:Upgrade] ${msg}`, data ?? '');

export class SolarmanRateLimitError extends Error {
  constructor(endpoint: string) {
    super(`Solarman rate limit exceeded on ${endpoint}`);
    this.name = 'SolarmanRateLimitError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class UpgradeService {
  constructor(private readonly solarmanService: SolarmanService) { }

  /**
   * lastAction is auxiliary (detects portal-driven upgrades in flight): any
   * failure — error body or thrown call — degrades to null and must never
   * break the assessment nor mark the device unsupported.
   *
   * TODO(one-click): approfondire `sorry_for_server_exception` — risulta FISSO
   * sui device senza storico ordini (logger con ordini → 200, inverter vergine
   * → exception, stessa raffica/auth). Verificare l'ipotesi dopo il primo
   * ordine one-click sull'inverter e decidere la gestione definitiva
   * (segnalazione a Solarman?).
   */
  private async safeLastAction(deviceId: string, label: string) {
    try {
      const lastAction = await this.solarmanService.deviceLastActionRequest(deviceId);
      if ((lastAction as any)?.code != null) {
        upgLog(` ${label} lastAction unavailable (code=${(lastAction as any).code}) → proceeding without`);
        return null;
      }
      return lastAction;
    } catch (error) {
      upgLog(` ${label} lastAction failed → proceeding without: ${(error as Error)?.message}`);
      return null;
    }
  }

  async getCommunicationInfoFromInverterSerial(serial: string): Promise<Communication> {
    await this.solarmanService.authenticate();
    let info;
    try {
      upgLog(` [${serial}] Solarman deviceInfo (1st attempt)`);
      info = await this.solarmanService.deviceInfoRequest(serial);
    } catch {
      upgLog(` [${serial}] Solarman deviceInfo failed, adding logger and retrying`);
      await this.solarmanService.addLoggerToSolarmanByInverterSerial(serial);
      info = await this.solarmanService.deviceInfoRequest(serial);
    }

    upgLog(` [${serial}] deviceInfo response:`, JSON.stringify(info));

    if ((info as any)?.code === 'OVER_MAXIMUM_LIMITS') {
      throw new SolarmanRateLimitError('/device/v1.0/communication');
    }

    if (info?.communication) {
      return info.communication;
    }

    throw new Error('No communication info found');
  }

  async getLoggerFwListByInverterSerial(
    serial: string,
    updatePercentage?: (percentage: number) => void,
    cachedCommunication?: Communication,
    options?: { skipFirmwareList?: boolean },
  ) {
    upgLog(` getLoggerFwListByInverterSerial START for ${serial}`);
    await this.solarmanService.authenticate();
    const communication = cachedCommunication ?? await this.getCommunicationInfoFromInverterSerial(serial);
    const isLoggerOnline = [SolarmanDeviceStatus.Online, SolarmanDeviceStatus.Alarm].includes(communication.deviceState);

    updatePercentage?.(25);

    upgLog(` [${serial}] Solarman deviceDetails for logger deviceId=${communication.deviceId}`);
    const communicationFwDetails = await this.solarmanService.deviceDetailsRequest(`${communication.deviceId}`);
    upgLog(` [${serial}] logger deviceDetails response:`, JSON.stringify(communicationFwDetails));
    if ((communicationFwDetails as any).code === 'OVER_MAXIMUM_LIMITS') {
      throw new SolarmanRateLimitError('/device-s/device/detail (logger)');
    }
    const currentModuleVersion = communicationFwDetails.paramCategoryList
      ?.find(({ name }) => name === 'Version Information')
      ?.fieldList.find(({ key }) => key === 'Module Version No')?.value;

    updatePercentage?.(50);

    if (!currentModuleVersion) {
      throw new Error('No current module version found');
    }

    upgLog(` [${serial}] Solarman lastAction for logger deviceId=${communication.deviceId}`);
    const lastAction = await this.safeLastAction(`${communication.deviceId}`, `[${serial}] logger`);
    upgLog(` [${serial}] logger lastAction response:`, JSON.stringify(lastAction));

    updatePercentage?.(75);

    let firmwareList: CallPROSolarmanFirmwareDetailsResponse[] = [];
    if (!options?.skipFirmwareList) {
      upgLog(` [${serial}] Solarman firmwareList for productId=${communicationFwDetails.productId}`);
      firmwareList = await this.solarmanService.productFirmwareListRequest(communicationFwDetails.productId);
      upgLog(` [${serial}] logger firmwareList response:`, JSON.stringify(firmwareList));
    }

    updatePercentage?.(100);
    upgLog(` getLoggerFwListByInverterSerial DONE for ${serial}: isLoggerOnline=${isLoggerOnline}, moduleVersion=${currentModuleVersion}`);
    return { firmwareList, communicationFwDetails, lastAction, isLoggerOnline };
  }

  async getInverterFwListByInverterSerial(
    serial: string,
    updatePercentage?: (percentage: number) => void,
    cachedCommunication?: Communication,
    options?: { skipFirmwareList?: boolean },
  ) {
    upgLog(` getInverterFwListByInverterSerial START for ${serial}`);
    await this.solarmanService.authenticate();
    const communication = cachedCommunication ?? await this.getCommunicationInfoFromInverterSerial(serial);
    const isLoggerOnline = [SolarmanDeviceStatus.Online, SolarmanDeviceStatus.Alarm].includes(communication.deviceState);

    updatePercentage?.(25);

    if (!communication.childList?.length) {
      throw new Error('No inverters found');
    }

    const inverter = communication.childList?.find((x) => x.deviceType === 'INVERTER' && x.deviceSn.toUpperCase() === serial.toUpperCase());
    if (!inverter) {
      throw new Error('Invalid inverter serial');
    }

    upgLog(` [${serial}] Solarman deviceDetails for inverter deviceId=${inverter.deviceId}`);
    const inverterFwDetails = await this.solarmanService.deviceDetailsRequest(`${inverter.deviceId}`);
    upgLog(` [${serial}] inverter deviceDetails response:`, JSON.stringify(inverterFwDetails));
    if ((inverterFwDetails as any).code === 'OVER_MAXIMUM_LIMITS') {
      throw new SolarmanRateLimitError('/device-s/device/detail (inverter)');
    }
    const isInverterOnline = [SolarmanDeviceStatus.Online, SolarmanDeviceStatus.Alarm].includes(inverterFwDetails.deviceState);
    const currentModuleVersions = inverterFwDetails.paramCategoryList
      ?.find(({ name }) => name === 'Version Information')?.fieldList;

    updatePercentage?.(50);

    if (!currentModuleVersions) {
      throw new Error('No current module version found');
    }

    upgLog(` [${serial}] Solarman lastAction for inverter deviceId=${inverter.deviceId}`);
    const lastAction = await this.safeLastAction(`${inverter.deviceId}`, `[${serial}] inverter`);
    upgLog(` [${serial}] inverter lastAction response:`, JSON.stringify(lastAction));

    updatePercentage?.(75);

    let firmwareList: CallPROSolarmanFirmwareDetailsResponse[] = [];
    if (!options?.skipFirmwareList) {
      upgLog(` [${serial}] Solarman firmwareList for productId=${inverterFwDetails.productId}`);
      firmwareList = await this.solarmanService.productFirmwareListRequest(inverterFwDetails.productId);
      upgLog(` [${serial}] inverter firmwareList response:`, JSON.stringify(firmwareList));
    }

    updatePercentage?.(100);
    upgLog(` getInverterFwListByInverterSerial DONE for ${serial}: isLoggerOnline=${isLoggerOnline}, isInverterOnline=${isInverterOnline}`);

    return { firmwareList, inverterFwDetails, lastAction, isLoggerOnline, isInverterOnline };
  }

  async getProductFirmwareList(productId: string): Promise<CallPROSolarmanFirmwareDetailsResponse[]> {
    await this.solarmanService.authenticate();
    return this.solarmanService.productFirmwareListRequest(productId);
  }

  async upgradeLoggerFw(
    communicationFwDetails: CallPROSolarmanDeviceDetailsResponse,
    fwVersion: CallPROSolarmanFirmwareDetailsResponse,
    timeout: number,
  ) {
    await this.solarmanService.authenticate();

    const currentVersion = communicationFwDetails.paramCategoryList.find(({ name }) => name === 'Version Information')
      ?.fieldList.find(({ key }) => key === 'Module Version No')?.value;

    if (currentVersion === fwVersion.version) {
      throw new Error('Current version is the same as the new version');
    }

    const { deviceId, deviceSn, productId } = communicationFwDetails;
    const { id } = fwVersion;

    return await this.solarmanService.deviceUpgradeRequest(
      deviceId,
      deviceSn,
      id,
      deviceId,
      deviceSn,
      timeout,
      productId,
    );
  }

  async upgradeInverterFw(
    inverterFwDetails: CallPROSolarmanDeviceDetailsResponse,
    fwVersion: CallPROSolarmanFirmwareDetailsResponse,
    timeout: number,
  ) {
    await this.solarmanService.authenticate();

    const fwVersionKey = fwVersion.versionKey;
    const fwVersionValue = fwVersion.version;

    const currentVersion = inverterFwDetails.paramCategoryList.find(({ name }) => name === 'Version Information')
      ?.fieldList.find(({ storageName }) =>  storageName === fwVersionKey)?.value;

    if (currentVersion === fwVersionValue) {
      throw new Error('Current version is the same as the new version');
    }

    const { deviceId, deviceSn, productId, parDeviceId, parDeviceSn } = inverterFwDetails;
    const { id } = fwVersion;

    return await this.solarmanService.deviceUpgradeRequest(
      deviceId,
      deviceSn,
      id,
      parDeviceId,
      parDeviceSn,
      timeout,
      productId,
    );
  }
}
